import { createFileRoute } from "@tanstack/react-router"
import { CircleCheck, CircleX } from "lucide-react"

import { TRIAL_DAYS } from "@/brand/plan-numbers"
import config from "@/config"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import {
  capKm,
  displayYear,
  EXAMPLE_TRIP,
  formatCheckedOn,
  HOME_FINANCIAL_YEAR,
  rate,
} from "../components/home/home-content"
import { AnswerBox, Breadcrumb, CtaBand, FaqAccordion, SourceLine } from "../components/landing"
import { GlanceCard, HeroBand } from "../components/landing/page-parts"
import { RIDESHARE_CHECKED, SRC } from "../content/pages/rideshare-sources"

/**
 * Copy source: docs/design/landing/copy.md and the web-landing-persona-rideshare design.
 * Delivery wording stays conservative until the Owner confirms (keyword-pages.md decision 4).
 * Every statement below was checked against the ATO page named next to it on RIDESHARE_CHECKED.
 */
const checked = formatCheckedOn(RIDESHARE_CHECKED)

export const rideshareSeo = () =>
  buildHead({
    title: "Rideshare and delivery drivers: ABN, GST, BAS",
    description:
      "What the ATO says rideshare and delivery drivers need: ABN, GST registration, BAS, car costs and records, each with its ATO source and checked date.",
    path: "/rideshare-drivers",
    jsonLd: [
      breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Rideshare and delivery drivers" }]),
    ],
  })

export const Route = createFileRoute("/_brand-layout/rideshare-drivers")({
  head: () => {
    const { meta, links, scripts } = rideshareSeo()
    return { meta, links, scripts }
  },
  component: RidesharePage,
})

const linkClass =
  "focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"

const FAQ = [
  {
    question: "Will the platform issue tax invoices for me?",
    answer: (
      <>
        It might. Once you are registered for GST, give the platform your ABN, as it may issue tax
        invoices for you.
        <SourceLine sources={[SRC.registrations]} checked={checked} />
      </>
    ),
  },
  {
    question: "When do I have to give a passenger a tax invoice?",
    answer: (
      <>
        If a passenger asks for a tax invoice for a fare over $82.50 including GST, you must provide
        one within 28 days. Fares under $82.50 only need an ordinary invoice.
        <SourceLine sources={[SRC.invoices]} checked={checked} />
      </>
    ),
  },
  {
    question: "Do I need to lodge a tax return?",
    answer: (
      <>
        The ATO says that because you are operating a business, you lodge a tax return regardless of
        how much you earn from ride-sourcing. Tips and bonuses from the platform count as income.
        <SourceLine sources={[SRC.income]} checked={checked} />
      </>
    ),
  },
]

export function RidesharePage() {
  const year = displayYear(HOME_FINANCIAL_YEAR)
  const rateChecked = formatCheckedOn(rate.checkedOn)
  return (
    <>
      <Breadcrumb
        items={[{ label: "Home", href: "/" }, { label: "Rideshare and delivery drivers" }]}
      />
      <HeroBand className="md:grid-cols-[1.4fr_1fr] md:gap-6">
        <div className="grid content-start gap-4">
          <p className="text-muted-foreground text-sm">Guide · Last reviewed {checked}</p>
          <h1 className="text-3xl font-bold tracking-tight md:text-5xl">
            Rideshare and delivery drivers: ABN, GST, BAS and car costs
          </h1>
          <AnswerBox source={<SourceLine sources={[SRC.registrations]} checked={checked} />}>
            If you drive a car for a rideshare platform you need an ABN and must be registered for
            GST before your first trip, whatever you earn. For delivery work the ATO&apos;s guidance
            depends on how you are engaged, so check its sharing-economy pages for your situation.
            Then you report GST on a BAS and can claim car costs.
          </AnswerBox>
        </div>
        <div
          className="border-border bg-background grid content-start gap-2 rounded-2xl border p-5 shadow-sm"
          role="img"
          aria-label={`Example trip card: ${EXAMPLE_TRIP.route}, ${EXAMPLE_TRIP.km} km at ${rate.cents} c/km, ${EXAMPLE_TRIP.amount}.`}
        >
          <span aria-hidden="true" className="text-muted-foreground text-sm">
            Example trip, recorded by GPS
          </span>
          <b aria-hidden="true" className="text-lg">
            {EXAMPLE_TRIP.route}
          </b>
          <span aria-hidden="true" className="text-muted-foreground">
            {EXAMPLE_TRIP.km} km × {rate.cents} c/km
          </span>
          <span aria-hidden="true" className="text-xl font-bold">
            {EXAMPLE_TRIP.amount}
          </span>
        </div>
      </HeroBand>

      <section className="px-band grid gap-4 py-8" aria-labelledby="glance-title">
        <h2 id="glance-title" className="text-2xl font-semibold">
          At a glance
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <GlanceCard
            icon={CircleCheck}
            tone="success"
            mark="check"
            title="You can"
            items={[
              "Claim business use of your car with cents per km or the logbook method",
              `Claim up to ${capKm} business km per car at ${rate.cents} cents for ${year}`,
            ]}
          />
          <GlanceCard
            icon={CircleX}
            tone="warning"
            mark="cross"
            title="You cannot"
            items={[
              "Wait for the $75,000 GST threshold if you drive rideshare: register before your first trip",
              "Use cents per km or a logbook for a vehicle that is not a car: it uses actual costs",
            ]}
          />
        </div>
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="abn-title">
        <h2 id="abn-title" className="text-2xl font-semibold">
          Do I need an ABN?
        </h2>
        <p className="max-w-[70ch]">
          Rideshare drivers do: you must have an ABN to register for GST. For delivery work, check
          the ATO&apos;s{" "}
          <a href={SRC.services.href} className={linkClass}>
            page on providing services through a platform
          </a>{" "}
          for your situation.
        </p>
        <SourceLine sources={[SRC.registrations]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="gst-title">
        <h2 id="gst-title" className="text-2xl font-semibold">
          GST
        </h2>
        <p className="max-w-[70ch]">
          Rideshare counts as taxi travel for GST. The usual $75,000 turnover threshold does not
          apply, so you register for GST before your first trip. GST applies to the full fare, and
          you choose to report monthly or quarterly. You cannot choose annual reporting.
        </p>
        <SourceLine sources={[SRC.registrations, SRC.gst]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="bas-title">
        <h2 id="bas-title" className="text-2xl font-semibold">
          BAS
        </h2>
        <p className="max-w-[70ch]">
          Once you are registered you report and pay the GST you collected on a business activity
          statement (BAS), and can claim GST credits for business purchases.{" "}
          {config.core.projectName} shows your trips and receipts; it does not lodge your BAS.
        </p>
        <SourceLine sources={[SRC.gst]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="car-title">
        <h2 id="car-title" className="text-2xl font-semibold">
          Car costs
        </h2>
        <p className="max-w-[70ch]">
          For a car you can use cents per kilometre (up to {capKm} business km per car, {rate.cents}{" "}
          cents for {year}) or the logbook method. Vehicles that are not cars use the actual costs
          method.
        </p>
        <p className="max-w-[70ch]">
          Only business kilometres count, and it depends on the purpose of the trip. In the
          ATO&apos;s examples, with the app on during your commute only the kilometres from
          accepting a job to drop-off count, while driving around only to find fares counts in full.
        </p>
        <SourceLine sources={[SRC.methods, SRC.income]} checked={checked} />
        <SourceLine
          sources={[{ label: "ATO, cents per kilometre rates", href: rate.sourceUrl }]}
          financialYear={year}
          checked={rateChecked}
        />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="records-title">
        <h2 id="records-title" className="text-2xl font-semibold">
          Records
        </h2>
        <p className="max-w-[70ch]">
          The ATO lists platform statements showing your income, receipts for expenses you claim,
          and logbooks and odometer readings. It says to keep records for 5 years after you lodge
          your tax return.
        </p>
        <SourceLine sources={[SRC.records, SRC.income]} checked={checked} />
      </section>

      <section className="px-band grid gap-3 py-8" aria-labelledby="delivery-title">
        <h2 id="delivery-title" className="text-2xl font-semibold">
          Delivery drivers
        </h2>
        <p className="max-w-[70ch]">
          The ATO lists delivery and courier services, such as food delivery, as sharing-economy
          activities. What you earn is assessable income that goes in your tax return. Whether you
          need to register for GST depends on how you are engaged and your turnover, so check the
          ATO pages or ask a registered tax agent before you decide.
        </p>
        <SourceLine sources={[SRC.activities, SRC.services, SRC.howGst]} checked={checked} />
      </section>

      <section className="px-band grid gap-4 py-8" aria-labelledby="faq-title">
        <h2 id="faq-title" className="text-2xl font-semibold">
          Common questions
        </h2>
        <FaqAccordion items={FAQ} />
      </section>

      <CtaBand
        title="Keep your driving records straight"
        description={`${TRIAL_DAYS}-day free trial. No card needed.`}
        ctaLabel="Start free trial"
        ctaHref="/login"
      />
    </>
  )
}
